// Need to use the React-specific entry point to import createApi
import axios from 'axios';
import { persistor, store } from '../store';
import { notification } from '../../utils/notification';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
import { selectApiLocality } from '../utils/apiLocality';

// Public/standalone pages that must never be bounced to the login screen when a
// background request (e.g. the is-logged-in check) returns 401. The contact form
// is opened by anonymous visitors via a QR code, so it has no session by design.
const PUBLIC_PAGE_PATHS = ['/auth', '/contact-us'];

const isPublicPagePath = (pathname = '') =>
  PUBLIC_PAGE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

const redirectToErrorPage = async () => {
  if (!isPublicPagePath(window.location?.pathname || '')) {
    notification('Session Expired', TOAST_TYPE.TOAST_ERROR);
    window.history.replaceState(null, '', '/auth');
    persistor.pause();
    persistor.flush().then(() => {
      return persistor.purge();
    });
    window.location.reload();
  }
};

const LOCALITY_SKIP_PATHS = ['/attendance-violation/my-warnings'];

const AUTH_NO_SESSION_REDIRECT_PATHS = ['/auth/pms-login', '/auth/google', '/auth/verify-otp'];

const shouldAppendLocality = (url) => !LOCALITY_SKIP_PATHS.some((path) => url.includes(path));

const shouldRedirectSessionOn401 = (url) =>
  !AUTH_NO_SESSION_REDIRECT_PATHS.some((path) => String(url || '').includes(path));

const appendLocalityToUrl = (url, locality) => {
  if (!shouldAppendLocality(url) || !locality?.length || url.includes('locality=')) {
    return url;
  }

  const localityQuery = locality.map((value) => `locality=${encodeURIComponent(value)}`).join('&');
  const separator = url.includes('?') ? '&' : '?';

  return `${url}${separator}${localityQuery}`;
};

export const axiosBaseQuery = ({ baseUrl, credentials } = { baseUrl: '', credentials: false }) => {
  return async ({ url, method, data, headers, params }) => {
    headers = { ...headers, 'Cache-Control': 'no-store' };

    const isGetRequest = !method || method.toLowerCase() === 'get';
    let requestUrl = url;

    if (isGetRequest) {
      const locality = selectApiLocality(store.getState());
      requestUrl = appendLocalityToUrl(url, locality);
    }

    try {
      const result = await axios({
        url: baseUrl + requestUrl,
        method,
        data,
        headers,
        params,
        withCredentials: credentials
      });
      return { data: result.data };
    } catch (axiosError) {
      if (
        axiosError?.response?.data?.statusCode === 401 &&
        shouldRedirectSessionOn401(requestUrl)
      ) {
        redirectToErrorPage();
      }

      if (axiosError?.isAxiosError && axiosError.response) {
        return { error: { status: axiosError.response?.status, data: axiosError.response?.data } };
      } else if (axiosError.request) {
        return { error: { message: 'Network error' } };
      } else {
        return { error: { message: 'Unexpected error' } };
      }
    }
  };
};
